// src/components/Modal.js
import React, { useEffect, useRef } from 'react';
import { FiX } from 'react-icons/fi';

/**
 * Universal Modal Component
 * Note: Escape key closing is managed globally by the App.js Root Component 
 * to ensure that nested modals safely close top-down.
 */
const Modal = ({ 
    isOpen, 
    onClose, 
    title, 
    children, 
    footer, 
    size = 'standard',
    className = '',
    contentStyle = {},
    zIndex = 1200
}) => {
    const overlayRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            // Prevent background scrolling while modal is open
            document.body.style.overflow = 'hidden';
        }

        return () => {
            // Only restore overflow if NO visible modals remain in the DOM
            const remainingOverlays = document.querySelectorAll('.modal-overlay');
            const hasVisible = Array.from(remainingOverlays).some(el => {
                const style = window.getComputedStyle(el);
                return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
            });
            
            if (!hasVisible) {
                document.body.style.overflow = 'unset';
            }
        };
    }, [isOpen]);

    if (!isOpen) return null;

    // Handle clicking the dimmed backdrop
    const handleBackdropClick = (e) => {
        if (e.target === overlayRef.current) {
            onClose();
        }
    };

    // Map size prop to CSS classes
    const sizeClasses = {
        'slim': 'modal-content-slim',
        'wide': 'modal-content-wide',
        'large': 'modal-content-large',
        'standard': ''
    };

    return (
        <div 
            className="modal-overlay" 
            ref={overlayRef}
            onMouseDown={handleBackdropClick}
            style={{ zIndex }} 
        >
            <div 
                className={`modal-content ${sizeClasses[size] || ''} ${className}`} 
                style={contentStyle}
                // Stop clicks inside the modal from bubbling to the backdrop
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="modal-header">
                    <h3 className="modal-title">{title}</h3>
                    <button 
                        type="button" 
                        onClick={onClose} 
                        className="modal-close-btn" 
                        title="Close"
                    >
                        <FiX />
                    </button>
                </div>

                {/* Body */}
                <div className="modal-body">
                    {children}
                </div>

                {/* Footer */}
                {footer && (
                    <div className="modal-footer">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Modal;